// src/components/ambassador/AmbassadorMap.jsx
//
// /ambassador/map - the prospects around the ambassador. Prospects of the
// visible area come from GET /api/ambassador/prospects?bbox=… once zoomed
// in enough (the API caps an area at ~5 km); colours follow availability
// with a legend (utils/ambassadorStatus.js). Tapping one opens a card:
// distance, "Réserver", "Voir la fiche".
//
// The phone's position is asked once to centre the map and show distances;
// it stays on the phone - the server only receives a position at check-in.
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { MapContainer, TileLayer, CircleMarker, GeoJSON, useMap, useMapEvents } from 'react-leaflet';
import 'leaflet/dist/leaflet.css';
import L from 'leaflet';
import { Box, Paper, Stack, Chip, Typography, Button, Alert, MenuItem, TextField, IconButton, Collapse } from '@mui/material';
import { useAmbassador } from './AmbassadorApp';
import { ambassadorGet, ambassadorPost } from '../../utils/ambassadorApi';
import { AVAILABILITY, LEGEND, FACILITY_TYPES, TYPE_LABEL, distanceLabel, errorText } from '../../utils/ambassadorStatus';

const PARIS = [48.8566, 2.3522];
const MAX_SPAN = 0.05; // degrees - same cap as the API
const FILTERS = [['all', 'Tous'], ['available', 'Disponibles'], ['follow_up', 'Mes relances'], ['never_visited', 'Jamais visités']];

/** Reports the visible area after every pan / zoom (and once at start). */
function AreaWatcher({ onArea }) {
  const map = useMapEvents({ moveend: () => onArea(map.getBounds()) });
  useEffect(() => { onArea(map.getBounds()); }, []); // eslint-disable-line react-hooks/exhaustive-deps
  return null;
}

/** Centres the map once: my position, else my territories, else Paris. */
function InitialView({ position, territories }) {
  const map = useMap();
  const done = useRef(false);
  useEffect(() => {
    if (done.current) return;
    if (position) { map.setView([position.lat, position.lon], 16); done.current = true; return; }
    const shapes = territories.filter((t) => t.polygon);
    if (shapes.length) {
      const b = L.geoJSON({ type: 'FeatureCollection', features: shapes.map((t) => ({ type: 'Feature', geometry: t.polygon })) }).getBounds();
      if (b.isValid()) map.fitBounds(b, { maxZoom: 16 });
    }
  }, [map, position, territories]);
  return null;
}

export default function AmbassadorMap() {
  const { me, getToken, reloadMe } = useAmbassador();
  const navigate = useNavigate();
  const [position, setPosition] = useState(null);
  const [area, setArea] = useState(null);
  const [filter, setFilter] = useState('all');
  const [type, setType] = useState('');
  const [prospects, setProspects] = useState([]);
  const [tooWide, setTooWide] = useState(false);
  const [truncated, setTruncated] = useState(false);
  const [selected, setSelected] = useState(null);
  const [legendOpen, setLegendOpen] = useState(false);
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!navigator.geolocation) return;
    navigator.geolocation.getCurrentPosition(
      (p) => setPosition({ lat: p.coords.latitude, lon: p.coords.longitude }),
      () => {}, { enableHighAccuracy: true, timeout: 10_000, maximumAge: 60_000 });
  }, []);

  const onArea = useCallback((b) => setArea(b), []);

  useEffect(() => {
    if (!area) return;
    const span = Math.max(area.getNorth() - area.getSouth(), area.getEast() - area.getWest());
    if (span > MAX_SPAN) { setTooWide(true); setProspects([]); return; }
    setTooWide(false);
    const bbox = [area.getSouth(), area.getWest(), area.getNorth(), area.getEast()].map((n) => n.toFixed(6)).join(',');
    const q = new URLSearchParams({ bbox, filter, ...(type ? { type } : {}) });
    let cancelled = false;
    ambassadorGet(`/prospects?${q}`, getToken())
      .then((d) => { if (!cancelled) { setProspects(d.prospects); setTruncated(d.truncated); } })
      .catch((e) => { if (!cancelled) setError(errorText(e)); });
    return () => { cancelled = true; };
  }, [area, filter, type, getToken]);

  const reserve = async (p) => {
    setBusy(true); setError('');
    try {
      await ambassadorPost(`/prospects/${p.id}/reserve`, getToken());
      await reloadMe();
      navigate(`/ambassador/prospects/${p.id}`);
    } catch (e) {
      setError(errorText(e));
      setSelected(null);
      setArea((a) => (a ? L.latLngBounds(a.getSouthWest(), a.getNorthEast()) : a)); // refresh colours
    } finally { setBusy(false); }
  };

  const mine = (a) => a === 'RESERVED_BY_ME' || a === 'FOLLOW_UP_MINE';
  const reservable = (a) => a === 'AVAILABLE' || a === 'FOLLOW_UP_MINE';

  return (
    <Box sx={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 56 }}>
      <MapContainer center={PARIS} zoom={15} style={{ height: '100%', width: '100%' }} zoomControl={false}>
        <TileLayer url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png" attribution="&copy; OpenStreetMap contributors" />
        <AreaWatcher onArea={onArea} />
        <InitialView position={position} territories={me.territories} />
        {me.territories.filter((t) => t.polygon).map((t) => (
          <GeoJSON key={t.id} data={t.polygon} interactive={false} style={{ color: '#0b4dff', weight: 2, dashArray: '6 6', fillOpacity: 0.03 }} />
        ))}
        {position && (
          <CircleMarker center={[position.lat, position.lon]} radius={7} interactive={false}
            pathOptions={{ color: '#ffffff', weight: 3, fillColor: '#111827', fillOpacity: 1 }} />
        )}
        {prospects.map((p) => {
          const meta = AVAILABILITY[p.availability] ?? AVAILABILITY.DO_NOT_CONTACT;
          return (
            <CircleMarker key={p.id} center={[p.lat, p.lon]} radius={mine(p.availability) ? 11 : 9}
              pathOptions={{ color: mine(p.availability) ? '#111827' : '#ffffff', weight: mine(p.availability) ? 3 : 2, fillColor: meta.color, fillOpacity: 0.95 }}
              eventHandlers={{ click: () => setSelected(p) }} />
          );
        })}
      </MapContainer>

      {/* Filters */}
      <Paper elevation={3} sx={{ position: 'absolute', top: 8, left: 8, right: 8, zIndex: 1000, p: 1 }}>
        <Stack direction="row" spacing={0.75} sx={{ overflowX: 'auto', pb: 0.5 }}>
          {FILTERS.map(([k, label]) => (
            <Chip key={k} label={label} size="small" color={filter === k ? 'primary' : 'default'} onClick={() => setFilter(k)} />
          ))}
        </Stack>
        <Stack direction="row" spacing={1} alignItems="center" sx={{ mt: 0.5 }}>
          <TextField select size="small" value={type} onChange={(e) => setType(e.target.value)} sx={{ minWidth: 140 }}
            SelectProps={{ displayEmpty: true }}>
            <MenuItem value="">Tous les types</MenuItem>
            {FACILITY_TYPES.map(([k, label]) => <MenuItem key={k} value={k}>{label}</MenuItem>)}
          </TextField>
          <Button size="small" onClick={() => setLegendOpen(!legendOpen)}>{legendOpen ? 'Masquer la légende' : 'Légende'}</Button>
        </Stack>
        <Collapse in={legendOpen}>
          <Stack spacing={0.5} sx={{ mt: 1 }}>
            {LEGEND.map(([color, label]) => (
              <Stack key={color} direction="row" spacing={1} alignItems="center">
                <Box sx={{ width: 14, height: 14, borderRadius: '50%', bgcolor: color, border: '2px solid #fff', boxShadow: '0 0 0 1px #d1d5db' }} />
                <Typography variant="body2">{label}</Typography>
              </Stack>
            ))}
            <Typography variant="caption" color="text.secondary">Contour noir : à moi. Point noir : ma position.</Typography>
          </Stack>
        </Collapse>
        {tooWide && <Typography variant="body2" sx={{ mt: 1 }}>Zoomez pour voir les établissements.</Typography>}
        {truncated && <Typography variant="body2" sx={{ mt: 1 }}>Trop d’établissements ici : zoomez pour tous les voir.</Typography>}
        {error && <Alert severity="warning" sx={{ mt: 1 }} onClose={() => setError('')}>{error}</Alert>}
      </Paper>

      {/* Selected prospect */}
      {selected && (
        <Paper elevation={6} sx={{ position: 'absolute', left: 8, right: 8, bottom: 8, zIndex: 1000, p: 2 }}>
          <Stack direction="row" justifyContent="space-between" alignItems="flex-start">
            <Box sx={{ minWidth: 0 }}>
              <Typography variant="h6" sx={{ lineHeight: 1.2 }}>{selected.name}</Typography>
              <Typography variant="body2" color="text.secondary">
                {[selected.address, selected.postalCode].filter(Boolean).join(' ')}
                {selected.facilityType ? ` · ${TYPE_LABEL[selected.facilityType] ?? selected.facilityType}` : ''}
              </Typography>
            </Box>
            <IconButton size="small" onClick={() => setSelected(null)} aria-label="Fermer">✕</IconButton>
          </Stack>
          <Stack direction="row" spacing={1} alignItems="center" sx={{ my: 1 }} flexWrap="wrap" useFlexGap>
            <Chip size="small" label={AVAILABILITY[selected.availability]?.label ?? selected.availability}
              sx={{ bgcolor: AVAILABILITY[selected.availability]?.color, color: '#fff', fontWeight: 600 }} />
            {distanceLabel(position, selected) && <Typography variant="body2">{distanceLabel(position, selected)}</Typography>}
            {selected.visits > 0 && <Typography variant="body2" color="text.secondary">{selected.visits} visite(s)</Typography>}
          </Stack>
          <Stack direction="row" spacing={1}>
            {reservable(selected.availability) && (
              <Button variant="contained" disabled={busy} onClick={() => reserve(selected)} sx={{ flex: 1 }}>
                Réserver
              </Button>
            )}
            <Button variant="outlined" onClick={() => navigate(`/ambassador/prospects/${selected.id}`)} sx={{ flex: 1 }}>
              Voir la fiche
            </Button>
          </Stack>
        </Paper>
      )}
    </Box>
  );
}
